import { Space, StatusBadge } from '@procurify/ui'

import { Price } from '@webapp/core/components/Price'
import { type BillItemExceptionUnitCostVariance } from '@webapp/domain/accountsPayable/entities'
import { useProcurifyIntl } from '@webapp/hooks'
import { BillItemPopoverLayout } from './BillItemPopoverLayout'
import messages from './messages'

/**
 * The 'BillItemExceptionUnitCostVarianceContent' component is responsible for rendering the content of a bill item unit cost variance exception as the content of a WithPopover component.
 *
 * The exception object comes from the validation errors on the BillItem entity
 */

interface BillItemExceptionUnitCostVarianceContentProps {
  exception: BillItemExceptionUnitCostVariance
}

export const BillItemExceptionUnitCostVarianceContent = ({
  exception,
}: BillItemExceptionUnitCostVarianceContentProps) => {
  const intl = useProcurifyIntl()

  const renderPrice = (
    price: number,
    fontWeight: 'normal' | 'bold' = 'normal'
  ) => (
    <Price
      price={price}
      currency={exception.currencyName}
      mdp
      fontWeight={fontWeight}
    />
  )

  return (
    <BillItemPopoverLayout
      title={intl.formatMessage(messages.unitCostVarianceTitle)}
      bodyRows={[
        {
          id: 'bill-item-unit-cost',
          label: intl.formatMessage(messages.billingLabel),
          valueComponent: renderPrice(exception.billItemUnitCost),
        },
        {
          id: 'unbilled-po-item-unit-cost',
          label: intl.formatMessage(messages.poLabel),
          valueComponent: renderPrice(exception.unbilledPoItemUnitCost),
        },
      ]}
      footerRows={[
        {
          id: 'variance',
          label: intl.formatMessage(messages.varianceLabel),
          valueComponent: (
            <Space direction='horizontal' align='center'>
              {exception.variance > 0 ? (
                <StatusBadge
                  text={intl.formatMessage(messages.statusOverbilledLabel)}
                  type='error'
                />
              ) : (
                <StatusBadge
                  text={intl.formatMessage(messages.statusAttentionLabel)}
                  type='warning'
                />
              )}

              {renderPrice(exception.variance, 'bold')}
            </Space>
          ),
        },
      ]}
    />
  )
}
